export class ObjectUtils {
  private static hasOwn = (obj: object, prop: PropertyKey): boolean => {
    return Object.prototype.hasOwnProperty.call(obj, prop);
  };

  static hasProperty<X extends Record<string, never>, Y extends PropertyKey>(
    obj: X,
    prop: Y,
  ): obj is X & Record<Y, unknown> {
    return this.hasOwn(obj, prop);
  }

  static hasFields(obj: Record<string, any>): boolean {
    return Object.keys(obj).length > 0;
  }

  static areObjectsEqual(obj1: any, obj2: any): boolean {
    // Check if both objects are null or undefined
    if (obj1 === null || obj1 === undefined || obj2 === null || obj2 === undefined) {
      return obj1 === obj2;
    }

    // Check if the objects are of the same type
    if (typeof obj1 !== typeof obj2) {
      return false;
    }

    // If the objects are arrays, compare each element
    if (Array.isArray(obj1) && Array.isArray(obj2)) {
      if (obj1.length !== obj2.length) {
        return false;
      }

      for (let i = 0; i < obj1.length; i++) {
        if (!this.areObjectsEqual(obj1[i], obj2[i])) {
          return false;
        }
      }

      return true;
    }

    // If the objects are non-array objects, compare each property
    if (typeof obj1 === 'object') {
      const keys1 = Object.keys(obj1);
      const keys2 = Object.keys(obj2);

      if (keys1.length !== keys2.length) {
        return false;
      }

      for (const key of keys1) {
        if (!this.areObjectsEqual(obj1[key], obj2[key])) {
          return false;
        }
      }

      return true;
    }

    // For primitive types, perform a simple equality check
    return obj1 === obj2;
  }

  static areEqualByFirstObjectFields(obj1: any, obj2: any): boolean {
    // Get all keys from the first object
    if (obj1 == null || obj2 == null) {
      return obj1 == obj2;
    }

    const keys = Object.keys(obj1);

    for (const key of keys) {
      const val1 = obj1[key];
      const val2 = obj2[key];

      // Check if the value is an object and not null
      const areObjects = ObjectUtils.isObject(val1) && ObjectUtils.isObject(val2);

      if (areObjects) {
        // Recursively compare nested objects
        if (!ObjectUtils.areEqualByFirstObjectFields(val1, val2)) {
          return false;
        }
      } else {
        // Compare primitive values
        if (val1 !== val2) {
          return false;
        }
      }
    }
    return true;
  }

  static isObject(obj: any): boolean {
    return obj !== null && typeof obj === 'object';
  }

  static getPropertyValue(obj: any, property?: string): any {
    if (property == null) {
      return '';
    }
    if (property.trim() === '') {
      return obj;
    }

    const properties: string[] = property.split('.') ?? [];
    let value = obj;

    for (const prop of properties) {
      if (value == null || typeof value != 'object') {
        return undefined;
      }
      value = value[prop];
    }

    return value;
  }

  static createObject(fieldName: string, fieldValue: any): any {
    const fieldObject: any = {};
    fieldObject[fieldName] = fieldValue;
    return fieldObject;
  }

  static removeEmptyFieldsFrom = (obj: any) =>
    Object.entries({ ...obj })
      .filter(([key, val]) => val != null)
      .reduce((prev, curr) => ({ ...prev, [curr[0]]: curr[1] }), <any>{});

  static areAllFieldsEmpty(obj: any): boolean {
    if (!obj || typeof obj !== 'object') {
      // Handle invalid input or edge cases
      return true;
    }

    for (const key in obj) {
      const fieldValue = obj[key];

      if (fieldValue !== null && fieldValue !== undefined) {
        if (typeof fieldValue === 'string') {
          if (fieldValue.trim() !== '') {
            return false;
          }
        } else if (Array.isArray(fieldValue)) {
          if (fieldValue.length !== 0) {
            return false;
          }
        } else if (typeof fieldValue === 'object') {
          if (Object.keys(fieldValue).length !== 0) {
            return false;
          }
        } else {
          return false; // If the field is of another non-empty type
        }
      }
    }

    return true;
  }

  static cloneDeep(obj: any): any {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      const newArray = [];
      for (let i = 0; i < obj.length; i++) {
        newArray[i] = this.cloneDeep(obj[i]);
      }
      return newArray;
    }

    const newObj: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && obj[key] !== undefined) {
        newObj[key] = this.cloneDeep(obj[key]);
      }
    }

    return newObj;
  }

  static stringToObject<T = unknown>(str?: string): T | null {
    if (!str) {
      return null;
    }
    try {
      // Using the JSON.parse method to convert the string to an object
      return JSON.parse(str);
    } catch (error) {
      return null;
    }
  }
}
